import { computed } from 'vue';

/**
 * Composable for transforming search parameters based on search term presence.
 * When a search term exists, it applies search-specific parameters like relevance sorting.
 * When no search term exists, it uses default parameters.
 *
 * @param {Object} options - Configuration options
 * @param {import('vue').Ref|Function} options.searchTerm - The search term to check (reactive)
 * @param {Object} options.defaultParams - Default parameters when no search term
 * @param {Object} options.transformParams - Parameters to use when search term exists
 * @param {string} [options.searchFieldName='search_text'] - The name of the search field
 *
 * @returns {import('vue').ComputedRef} Reactive transformed parameters object
 *
 * @example
 * const searchParams = useSearchParams({
 *   searchTerm: () => filters.search_text,
 *   defaultParams: {
 *     sort_by: 'name',
 *     sort_order: 'asc',
 *     page: 5
 *   },
 *   transformParams: {
 *     sort_by: 'relevance',
 *     sort_order: 'desc'
 *   }
 * });
 */
export function useSearchAwareParams({
  searchTerm,
  defaultParams = {},
  transformParams = {},
  searchFieldName = 'search_text',
}) {
  return computed(() => {
    const currentSearchTerm = typeof searchTerm === 'function' ? searchTerm() : searchTerm;
    const hasSearchTerm = currentSearchTerm && currentSearchTerm.trim().length > 0;

    return hasSearchTerm
      ? { ...defaultParams, ...transformParams, [searchFieldName]: currentSearchTerm }
      : { ...defaultParams, [searchFieldName]: '' };
  });
}
